import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import Fill from '../../../../app/pages/InteractiveTest/components/Fill';
import FillDnd from '../../../../app/pages/InteractiveTest/components/FillDnd';
import { PluginProps } from '../Plugin';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import { useQueryParam } from '../../../../../hooks/useQueryParams';
import {
  useInteractiveComponentStateQuery,
  useSaveInteractiveComponentStateMutation,
} from '../../../../../generated/graphqlModels';
import { MD5 } from '../../../../../helpers/utils';
import SelectUser from '../../SelectUser';
import Loader from '../../../../../components/Loader';
import { GET_INTERACTIVE_COMPONENT_STATE } from '../../../../app/pages/InteractiveTest/components/shared/queries';
import { useIsMobile } from '../../../../../hooks/isMobile';

const FillPlugin = (props: PluginProps) => {
  const { t } = useTranslation();
  const { content, studyGroup } = props;
  const contentData = content.data;
  const user = useContext(AuthContext);
  const debugParam = useQueryParam('debug');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    user?.pk || null,
  );
  const [showCorrect, setShowCorrect] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isInitialMount = useRef(true);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setShowCorrect(false);

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    setIsTransitioning(true);
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }
    transitionTimeoutRef.current = setTimeout(() => {
      setIsTransitioning(false);
      transitionTimeoutRef.current = null;
    }, 500);

    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, [selectedUserId]);

  const debug = user?.roles.includes(Role.staff) && !!debugParam;

  const type = contentData.type;

  const studyGroupId = studyGroup.pk;

  const queryVariables = {
    userId: selectedUserId! || user?.pk!,
    studyGroupId: studyGroupId!,
    componentId: contentData.unique_id,
  };

  const { data, loading } = useInteractiveComponentStateQuery({
    variables: queryVariables,
    skip: user == null || user.pk == null || studyGroupId == null,
  });

  const [
    saveState,
    // { data: mutData, loading: mutLoading, error: mutError }
  ] = useSaveInteractiveComponentStateMutation({
    refetchQueries: [
      {
        query: GET_INTERACTIVE_COMPONENT_STATE,
        variables: queryVariables,
      },
    ],
  });

  const hash = useMemo(() => {
    return MD5.hash(JSON.stringify(contentData.config));
  }, [contentData.config]);

  const submit = (incorrect: number, total: number, state: any) => {
    // console.log({ state });
    saveState({
      variables: {
        data: {
          componentId: contentData.unique_id,
          userId: user!.pk,
          studyGroupId: studyGroupId!,
          hash,
          state,
        },
        reset: false,
      },
    });
  };

  const reset = () => {
    saveState({
      variables: {
        data: {
          componentId: contentData.unique_id,
          userId: user!.pk,
          studyGroupId: studyGroupId!,
          hash,
          state: '',
        },
        reset: true,
      },
    });
  };

  const isViewOnly = !!selectedUserId && selectedUserId !== user?.pk!;

  const ShowCorrect = () =>
    user?.roles.includes(Role.teacher) ? (
      <Button onClick={() => setShowCorrect(!showCorrect)} margin="10px">
        {showCorrect ? t('plugin.hideCorrect') : t('plugin.showCorrect')}
      </Button>
    ) : null;

  const isMobile = useIsMobile();

  const contentContainerStyle = {
    textAlign: 'center' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    gap: '10px',
    minHeight: '120px',
  };

  if (type === 'normal') {
    return (
      <>
        <SelectUser
          studyGroupId={studyGroupId}
          studentId={selectedUserId}
          setStudentId={setSelectedUserId}
        />
        <div style={contentContainerStyle}>
          {loading || isTransitioning ? (
            <Loader />
          ) : (
            <>
              <Fill
                key={`${contentData.id}-${selectedUserId ?? user?.pk ?? 'current'}`}
                text={contentData.text}
                slots={contentData.config}
                initialState={data?.getInteractiveContentState?.state}
                debug={debug}
                showCorrect={showCorrect}
                submit={submit}
                reset={reset}
                isViewOnly={isViewOnly}
              />
              <ShowCorrect />
            </>
          )}
        </div>
      </>
    );
  }
  if (type === 'dnd') {
    return (
      <>
        <SelectUser
          studyGroupId={studyGroupId}
          studentId={selectedUserId}
          setStudentId={setSelectedUserId}
        />
        <div style={contentContainerStyle}>
          {loading || isTransitioning ? (
            <Loader />
          ) : (
            <>
              <FillDnd
                key={`${contentData.id}-${selectedUserId ?? user?.pk ?? 'current'}`}
                text={contentData.text}
                slots={contentData.config}
                dndPosition={contentData.dnd_position}
                initialState={data?.getInteractiveContentState?.state}
                debug={debug}
                showCorrect={showCorrect}
                submit={submit}
                reset={reset}
                isViewOnly={isViewOnly}
              />
              <ShowCorrect />
            </>
          )}
        </div>
      </>
    );
  }
  return <Text>Error, unknown type '{type}'</Text>;
};

export default FillPlugin;
